<template>
    <div v-if="survey">
        <!-- Header -->
        <div class="flex items-center mb-6 ml-1">
            <BackButton class="mr-1" />
            <h1 class="text-2xl font-black font-lato mr-2">{{ survey.type }} Survey</h1>
            <div class="ml-auto">
                <!-- Main Spatial Intelligence Button -->
                <button 
                    @click="spatialIntelligenceEnabled = !spatialIntelligenceEnabled"
                    :class="[
                        'inline-flex items-center px-6 py-3 rounded-lg text-base font-medium transition-all duration-200',
                        spatialIntelligenceEnabled 
                            ? 'bg-[#27bdf4] text-white shadow-lg shadow-[#27bdf4]/25' 
                            : 'bg-black text-white hover:bg-gray-900'
                    ]"
                >
                    <i class="mdi mdi-creation-outline mr-2 text-lg"></i>
                    Spatial Intelligence
                </button>
            </div>
        </div>

        <!-- Survey Info -->

        <!-- Key Stats Header -->
        <div v-if="(survey.eventData?.surveyStatus === 'completed' || survey.eventData?.surveyStatus === 'done') && dataPath" class="mb-6">
            <SurveyStatsHeader :dataPath="dataPath" />
        </div>

        <!-- Main Content Area - Side by Side Layout -->
        <div v-if="(survey.eventData?.surveyStatus === 'completed' || survey.eventData?.surveyStatus === 'done') && dataPath" class="flex flex-col lg:flex-row gap-3 h-auto lg:h-[800px]">
            <!-- Left Side - 3D Model Viewer (takes most space) -->
            <div class="flex-1 bg-black rounded-lg shadow-md p-6 h-[500px] lg:h-auto">
                <h3 class="text-lg font-bold text-white mb-4 flex items-center">
                    <i class="mdi mdi-cube-outline mr-2"></i>
                    3D Reef Model
                </h3>
                <div class="h-[calc(100%-3rem)]">
                    <SurveyModel :dataPath="dataPath" :aiEnabled="spatialIntelligenceEnabled" />
                </div>
            </div>
            
            <!-- Right Side - Photo Gallery (narrow vertical on desktop, horizontal on mobile) -->
            <div class="w-full lg:w-80 bg-black rounded-lg shadow-md h-[600px] lg:h-auto">
                <SurveyPhotos :dataPath="dataPath" :isVerticalLayout="isDesktop" :showOverlays="spatialIntelligenceEnabled" />
            </div>
        </div>

        <!-- Processing/Error States for Model -->
        <div v-else-if="survey.eventData?.surveyStatus === 'pending' || survey.eventData?.surveyStatus === 'processing'" class="bg-black rounded-lg shadow-md p-6">
            <h3 class="text-lg font-bold text-white mb-4 flex items-center">
                <i class="mdi mdi-cube-outline mr-2"></i>
                3D Reef Model
            </h3>
            <div class="h-[800px] flex items-center justify-center bg-blue-900 rounded-lg">
                <div class="text-center text-white">
                    <i class="mdi mdi-cog mdi-spin text-4xl mb-4"></i>
                    <p class="text-lg font-medium">Processing 3D Model</p>
                    <p class="text-sm text-blue-200 mt-2">Photogrammetry reconstruction in progress...</p>
                </div>
            </div>
        </div>
        
        <div v-else class="bg-black rounded-lg shadow-md p-6">
            <h3 class="text-lg font-bold text-white mb-4 flex items-center">
                <i class="mdi mdi-cube-outline mr-2"></i>
                3D Reef Model
            </h3>
            <div class="h-[800px] flex items-center justify-center bg-gray-800 rounded-lg">
                <div class="text-center text-neutral-400">
                    <i class="mdi mdi-cube-off text-4xl mb-4"></i>
                    <p class="text-lg font-medium">No 3D Model Available</p>
                    <p class="text-sm mt-2">Model not yet processed or unavailable</p>
                </div>
            </div>
        </div>

        <!-- Detailed Reef Statistics - Show below main content if completed and has gcsPath -->
        <div v-if="(survey.eventData?.surveyStatus === 'completed' || survey.eventData?.surveyStatus === 'done') && dataPath">
            <SurveyStatsAll :dataPath="dataPath" />
        </div>
    </div>
    
    <!-- Loading State -->
    <!-- <div v-else class="flex items-center justify-center min-h-[400px]">
        <div class="text-center">
            <i class="mdi mdi-loading mdi-spin text-4xl text-primary mb-4"></i>
            <p class="text-neutral-400">Loading survey data...</p>
        </div>
    </div> -->
</template>

<script>
import { db_resto_new } from '../main';
import { doc, getDoc } from 'firebase/firestore';
import BackButton from './utils/BackButton.vue';
import SurveyModel from './SurveyModel.vue';
import SurveyStatsHeader from './SurveyStatsHeader.vue';
import SurveyStatsAll from './SurveyStatsAll.vue';
import SurveyPhotos from './SurveyPhotos.vue';

export default {
    name: 'Survey',
    components: {
        BackButton,
        SurveyModel,
        SurveyStatsHeader,
        SurveyStatsAll,
        SurveyPhotos
    },
    props: {
        branchId: String,
        outplantSiteId: String,
        surveyId: String,
    },
    data() {
        return {
            survey: null,
            outplantSiteName: null,
            loading: true,
            error: null,
            windowWidth: window.innerWidth,
            spatialIntelligenceEnabled: false
        };
    },
    mounted() {
        this.fetchSurveyData();
        window.addEventListener('resize', this.handleResize);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleResize);
    },
    computed: {
        isDesktop() {
            return this.windowWidth >= 1024; // lg breakpoint
        },
        dataPath() {
            // Extract the gcsPath from the new database structure
            return this.survey?.eventData?.surveyData?.gcsPath || null;
        }
    },
    watch: {
        '$route': {
            immediate: true,
            handler() {
                this.fetchSurveyData();
            }
        }
    },
    methods: {
        async fetchSurveyData() {
            try {
                this.loading = true;
                this.error = null;

                // Fetch survey data
                // ToDo - should this also filter by branch and outplant site?? 
                const surveyRef = doc(db_resto_new, `_events/${this.surveyId}`);
                const surveyDoc = await getDoc(surveyRef);

                if (surveyDoc.exists()) {
                    this.survey = {id: surveyDoc.id, ...surveyDoc.data()};
                    
                    // Also fetch outplant site name for context if outplantID exists
                    if (this.survey.location && this.survey.location.outplantID) {
                        await this.fetchOutplantSiteName(this.survey.location.outplantID);
                    }
                } else {
                    this.error = 'Survey not found';
                    console.error('No such survey!');
                }
            } catch (error) {
                this.error = 'Error loading survey data';
                console.error('Error fetching survey data:', error);
            } finally {
                this.loading = false;
            }
        },
        async fetchOutplantSiteName(outplantSiteId) {
            try {
                const outplantSiteRef = doc(db_resto_new, `_sites/${outplantSiteId}`);
                const outplantSiteDoc = await getDoc(outplantSiteRef);
                
                if (outplantSiteDoc.exists()) {
                    this.outplantSiteName = outplantSiteDoc.data().name;
                }
            } catch (error) {
                console.error('Error fetching outplant site name:', error);
            }
        },
        formatDate(date) {
            if (!date) return '';
            
            // Handle different date formats
            let dateObj;
            if (typeof date === 'string') {
                dateObj = new Date(date);
            } else if (date.toDate && typeof date.toDate === 'function') {
                // Firestore timestamp
                dateObj = date.toDate();
            } else if (date instanceof Date) {
                dateObj = date;
            } else {
                return '';
            }
            
            return dateObj.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            });
        },
        handleResize() {
            this.windowWidth = window.innerWidth;
        }
    }
}
</script>

<style scoped>
/* Additional styles for the survey detail page */
.grid {
    display: grid;
}

.grid-cols-1 {
    grid-template-columns: repeat(1, minmax(0, 1fr));
}

@media (min-width: 768px) {
    .md\:grid-cols-2 {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 1024px) {
    .lg\:grid-cols-3 {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}
</style>
